import { useEffect, useRef } from "react";

// Loads a script tag once (idempotent).
function ensureScript(src, globalName) {
  if (typeof window === "undefined") return;
  if (globalName && window[globalName]) return;
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  document.body.appendChild(s);
}

// Renders a real Instagram or TikTok post via the platform's official embed script.
// No API keys needed — these are the same scripts every blog/news site uses.
export default function SocialEmbed({ post }) {
  const ref = useRef(null);
  const platform = post.platform || (post.url?.includes("tiktok.com") ? "tiktok" : "instagram");

  useEffect(() => {
    if (platform === "instagram") {
      ensureScript("https://www.instagram.com/embed.js");
      // Re-process when ready; cap retries so adblockers don't loop forever.
      let tries = 0;
      const t = setInterval(() => {
        tries += 1;
        if (window.instgrm?.Embeds) {
          window.instgrm.Embeds.process();
          clearInterval(t);
        } else if (tries > 15) {
          clearInterval(t);
        }
      }, 400);
      return () => clearInterval(t);
    }
    if (platform === "tiktok") {
      ensureScript("https://www.tiktok.com/embed.js");
    }
  }, [platform, post.url]);

  if (!post.url) return null;

  if (platform === "tiktok") {
    const m = post.url.match(/\/video\/(\d+)/);
    const videoId = m ? m[1] : "";
    return (
      <div data-testid={`tiktok-embed-${post.id}`} className="w-full flex justify-center">
        <blockquote
          ref={ref}
          className="tiktok-embed bg-white rounded-2xl overflow-hidden"
          cite={post.url}
          data-video-id={videoId}
          style={{ maxWidth: 605, minWidth: 280, margin: 0 }}
        >
          <section>
            <a target="_blank" rel="noreferrer" href={post.url}>
              {post.handle || "View on TikTok"}
            </a>
            {post.caption && <p className="text-xs text-[#5C5547] p-3">{post.caption}</p>}
          </section>
        </blockquote>
      </div>
    );
  }

  return (
    <div data-testid={`instagram-embed-${post.id}`} className="w-full flex justify-center">
      <blockquote
        ref={ref}
        className="instagram-media bg-white rounded-2xl overflow-hidden"
        data-instgrm-permalink={post.url}
        data-instgrm-version="14"
        style={{
          background: "#FFF",
          border: 0,
          borderRadius: 16,
          margin: 0,
          maxWidth: 540,
          minWidth: 280,
          padding: 0,
          width: "100%",
        }}
      >
        <a target="_blank" rel="noreferrer" href={post.url}>
          {post.handle || "View on Instagram"}
        </a>
      </blockquote>
    </div>
  );
}
